import apiService from './apiService';

export type ConsentStatus = 'granted' | 'revoked' | 'never_asked';
export type ChannelPreference = 'voice' | 'whatsapp';

/** An inferred property. Rendered as an inference, never as a stated fact —
 * `label`/`value_label` are the German wording, `from_item_nm` says which
 * booked service it was read from. */
export interface TraitEntry {
  trait: string;
  value: string;
  label?: string | null;
  value_label?: string | null;
  confidence?: number | null;
  from_item_no?: number | null;
  from_item_nm?: string | null;
}

export interface CustomerListItem {
  id: number;
  name: string | null;
  phone: string | null;
  email: string | null;
  gender: string | null;
  bookings_total: number;
  bookings_cancelled: number;
  calls_total: number;
  whatsapp_sessions_total: number;
  last_contact_at: string | null;
  last_booking_at: string | null;
  channel_preference: ChannelPreference | null;
  traits: TraitEntry[];
  consent_status: ConsentStatus;
}

export interface CustomerListResponse {
  status: string;
  total: number;
  items: CustomerListItem[];
}

export interface TimelineEntry {
  kind: 'booking' | 'call' | 'whatsapp';
  at: string | null;
  title: string | null;
  detail: string | null;
  /** Set for calls and WhatsApp sessions — links into the Verläufe transcript. */
  conversation_id: number | null;
  status: string | null;
}

export interface TopItem {
  item_no: number;
  name: string | null;
  count: number;
}

export interface CustomerDetail extends CustomerListItem {
  created_at: string | null;
  first_booking_at: string | null;
  avg_days_between_bookings: number | null;
  preferred_employee: string | null;
  preferred_daypart: string | null;
  agent_numbers: string[];
  top_items: TopItem[];
}

export interface CustomerDetailResponse {
  status: string;
  customer: CustomerDetail;
  timeline: TimelineEntry[];
}

export type CustomerSort = 'last_contact' | 'bookings' | 'name';

const customerService = {
  list: async (params: {
    q?: string;
    agentNumber?: string | null;
    consent?: ConsentStatus | null;
    sort?: CustomerSort;
    limit?: number;
    offset?: number;
  } = {}): Promise<CustomerListResponse> => {
    const search = new URLSearchParams();
    if (params.q?.trim()) search.set('q', params.q.trim());
    // Without an agent number every customer is listed, including those whose
    // history predates per-number attribution.
    if (params.agentNumber) search.set('agent_number', params.agentNumber);
    if (params.consent) search.set('consent', params.consent);
    if (params.sort) search.set('sort', params.sort);
    search.set('limit', String(params.limit ?? 50));
    search.set('offset', String(params.offset ?? 0));
    return apiService.get<CustomerListResponse>(`/customers?${search.toString()}`);
  },

  getDetail: async (customerId: number): Promise<CustomerDetailResponse> =>
    apiService.get<CustomerDetailResponse>(`/customers/${customerId}`),
};

export default customerService;
